/**
 * My Bots -> [bot] (§7.1). Owner entry point: Console | Character | Accounts |
 * Schedule | Library | Listing | Activity.
 *
 * §7.4 rule 5: the capability set is re-fetched on a poll so a revocation shows
 * up in an open tab within 60s — 30s while a rental is active, since that is the
 * window in which grants actually change.
 */

import { NavLink, Outlet, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useCapabilities } from '../../hooks/useCapabilities';
import type { Bot } from '../../api/types';
import { ErrorView } from '../../components/ErrorView';
import { Loading } from '../../components/ui';
import type { BotOutlet } from '../botOutlet';
import { routes } from '../../api/routes';

const TABS = [
  { to: 'console', label: 'Console' },
  { to: 'character', label: 'Character' },
  { to: 'accounts', label: 'Accounts' },
  { to: 'schedule', label: 'Schedule' },
  { to: 'library', label: 'Library' },
  { to: 'listing', label: 'Listing' },
  { to: 'activity', label: 'Activity' },
];

export function BotLayout() {
  const { botId = '' } = useParams();
  const bot = useApi<Bot>(routes.bots.one(botId), { pollMs: 30_000 });
  const caps = useCapabilities(bot.data);

  if (bot.initialLoading) return <Loading label="Loading this bot" />;
  if (bot.problem) return <ErrorView problem={bot.problem} onRetry={bot.reload} />;
  if (!bot.data) return null;

  const context: BotOutlet = {
    bot: bot.data,
    caps,
    reload: bot.reload,
    scope: 'owner',
    rental: null,
  };

  /*
   * §7.4 rules 2 and 4: Messages is a *tab*, so an absent capability has to
   * remove it rather than render a section that 403s on entry. `read_dm`
   * resolves absent when no connected platform supports DM, which is the case
   * for a Bluesky-only bot — the tab disappears rather than leading somewhere
   * empty.
   */
  const tabs = caps.visible('read_dm')
    ? [...TABS.slice(0, 5), { to: 'dm', label: 'Messages' }, ...TABS.slice(5)]
    : TABS;

  return (
    <div className="bot-scope">
      <nav className="subnav" aria-label="Bot sections">
        <ul>
          {tabs.map((t) => (
            <li key={t.to}>
              <NavLink
                to={t.to}
                className={({ isActive }) => `subnav-link${isActive ? ' subnav-link-active' : ''}`}
              >
                {t.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <Outlet context={context} />
    </div>
  );
}
